import { type FC, useMemo, useState } from 'react'
import { InputText } from '@/components/commons/input-text'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { SearchLgIcon, XCloseIcon } from '@/components/ui/icons'
import { Modal } from '@/components/ui/modal'
import { cn } from '@/helpers/utils'
import {
	APP_LANGUAGES,
	changeAppLanguage,
	useTranslation
} from '@/translations/i18n'

interface LangModalProps {
	onCloseCb: () => void
}

export const LangSelectionModal: FC<LangModalProps> = ({ onCloseCb }) => {
	const {
		t,
		i18n: { language }
	} = useTranslation()
	const currentLanguage =
		APP_LANGUAGES.find((lang) => lang.code === language) ?? APP_LANGUAGES[0]
	const [search, setSearch] = useState('')

	const languagesList = useMemo(() => {
		return [
			currentLanguage,
			...APP_LANGUAGES.filter(
				(lang) => lang.code !== currentLanguage.code
			)
		].filter(
			(lang) =>
				lang.label.toLowerCase().includes(search.toLowerCase()) ||
				lang.code.toLowerCase().includes(search.toLowerCase())
		)
	}, [currentLanguage, search])

	return (
		<Modal onOverlayClick={onCloseCb} width='850px'>
			<div className='relative flex flex-col'>
				<div className='sticky top-0 flex w-full flex-wrap items-center gap-2 border-neutral-200 border-b bg-neutral-white p-4'>
					<div className='flex items-center gap-2'>
						<ButtonLegacy
							onClick={onCloseCb}
							shape='square'
							size='lg'
							type='button'
							variant='tertiary'
						>
							<XCloseIcon />
						</ButtonLegacy>
						<h3 className='shrink-0 font-bold text-neutral-900 text-xl'>
							{t('common:select_lang_title')}
						</h3>
					</div>

					{APP_LANGUAGES.length > 12 && (
						<InputText
							className='w-auto flex-grow'
							inputSize='sm'
							onChange={(event) => setSearch(event.target.value)}
							placeholder={String(t('common:search_languages'))}
							type='text'
							typeIcon={
								<SearchLgIcon
									className='fill-neutral-500'
									size={20}
								/>
							}
						/>
					)}
				</div>

				{languagesList.length >= 1 ? (
					<div className='grid h-max max-h-full w-full grid-cols-3 gap-6 overflow-y-auto p-6 sm:grid-cols-1 md:grid-cols-2'>
						{languagesList.map((lang) => (
							<button
								className={cn(
									'cursor-pointer rounded-lg px-4 py-2.5 text-neutral-900 text-sm transition-all duration-200',
									lang.code === currentLanguage.code
										? 'border border-black'
										: 'hover:bg-neutral-50'
								)}
								key={lang.code}
								onClick={() => changeAppLanguage(lang.code)}
								type='button'
							>
								{lang.label}
							</button>
						))}
					</div>
				) : (
					<div className='flex h-full w-full items-center justify-center p-6'>
						{t('common:no_language_results')}
					</div>
				)}
			</div>
		</Modal>
	)
}

LangSelectionModal.displayName = 'LangSelectionModal'
